// ============================================
// AlpenScreen Prompt Templates Library
// File: frontend/src/utils/promptTemplates.js
// Pre-defined templates for different industries
// ============================================

export const INDUSTRIES = [
  { id: 'general', name: 'Allgemein', icon: '🏢', nameEn: 'General' },
  { id: 'restaurant', name: 'Restaurant', icon: '🍽️', nameEn: 'Restaurant' },
  { id: 'fitness', name: 'Fitness', icon: '💪', nameEn: 'Fitness' },
  { id: 'retail', name: 'Einzelhandel', icon: '🛍️', nameEn: 'Retail' },
  { id: 'hotel', name: 'Hotel', icon: '🏨', nameEn: 'Hotel' },
  { id: 'healthcare', name: 'Gesundheit', icon: '🏥', nameEn: 'Healthcare' },
  { id: 'education', name: 'Bildung', icon: '📚', nameEn: 'Education' },
  { id: 'office', name: 'Büro', icon: '🏢', nameEn: 'Office' },
  { id: 'beauty', name: 'Beauty', icon: '💅', nameEn: 'Beauty' },
  { id: 'automotive', name: 'Automotive', icon: '🚗', nameEn: 'Automotive' }
];

export const TONE_PRESETS = [
  { id: 'professional', name: 'Professionell', description: 'Seriös und vertrauenswürdig', icon: '👔' },
  { id: 'casual', name: 'Locker', description: 'Freundlich und zugänglich', icon: '😊' },
  { id: 'energetic', name: 'Energiegeladen', description: 'Motivierend und dynamisch', icon: '⚡' },
  { id: 'elegant', name: 'Elegant', description: 'Raffiniert und stilvoll', icon: '✨' },
  { id: 'playful', name: 'Verspielt', description: 'Kreativ und unterhaltsam', icon: '🎨' },
  { id: 'luxury', name: 'Luxuriös', description: 'Exklusiv und hochwertig', icon: '💎' },
  { id: 'minimalist', name: 'Minimalistisch', description: 'Klar und prägnant', icon: '⬜' },
  { id: 'urgent', name: 'Dringend', description: 'Handlungsauffordernd', icon: '⏰' }
];

// Templates organized by industry
export const PROMPT_TEMPLATES = {
  restaurant: [
    {
      id: 'restaurant_daily_special',
      name: 'Tagesangebot',
      icon: '🍴',
      template: 'Tagesangebot {dish}',
      contentType: 'headline',
      tone: 'casual',
      example: 'Frischer Lachs mit Gemüse'
    },
    {
      id: 'restaurant_happy_hour',
      name: 'Happy Hour',
      icon: '🍹',
      template: 'Happy Hour Angebot für {drinks}',
      contentType: 'headline',
      tone: 'energetic',
      example: 'Cocktails und Longdrinks'
    },
    {
      id: 'restaurant_welcome',
      name: 'Willkommen',
      icon: '👋',
      template: 'Willkommens-Text für {restaurant_type}',
      contentType: 'description',
      tone: 'professional',
      example: 'italienisches Restaurant'
    },
    {
      id: 'restaurant_seasonal',
      name: 'Saison-Aktion',
      icon: '🌸',
      template: '{season} Special Menü',
      contentType: 'headline',
      tone: 'elegant',
      example: 'Sommer, Frühling, Herbst, Winter'
    },
    {
      id: 'restaurant_reservation',
      name: 'Reservierung',
      icon: '📞',
      template: 'Tischreservierung CTA',
      contentType: 'cta',
      tone: 'professional',
      example: ''
    }
  ],
  
  fitness: [
    {
      id: 'fitness_new_members',
      name: 'Neue Mitglieder',
      icon: '🎯',
      template: 'Neue Mitglieder werben für {gym_name}',
      contentType: 'headline',
      tone: 'energetic',
      example: 'FitZone Premium'
    },
    {
      id: 'fitness_class',
      name: 'Kurse',
      icon: '🧘',
      template: '{class_type} Kurs Ankündigung',
      contentType: 'description',
      tone: 'energetic',
      example: 'Yoga, Spinning, CrossFit'
    },
    {
      id: 'fitness_motivation',
      name: 'Motivation',
      icon: '💪',
      template: 'Motivierender Spruch für {goal}',
      contentType: 'headline',
      tone: 'energetic',
      example: 'Gewichtsverlust, Muskelaufbau'
    },
    {
      id: 'fitness_promo',
      name: 'Promo-Aktion',
      icon: '🎁',
      template: '{discount} Rabatt Aktion',
      contentType: 'headline',
      tone: 'urgent',
      example: '20%, 50%'
    }
  ],
  
  retail: [
    {
      id: 'retail_sale',
      name: 'Sale',
      icon: '🏷️',
      template: '{discount} Rabatt auf {product_category}',
      contentType: 'headline',
      tone: 'urgent',
      example: '30%, Elektronik, Mode'
    },
    {
      id: 'retail_new_arrival',
      name: 'Neuheiten',
      icon: '✨',
      template: 'Neue {product} eingetroffen',
      contentType: 'headline',
      tone: 'energetic',
      example: 'Kollektion, Produkte'
    },
    {
      id: 'retail_limited',
      name: 'Limited Edition',
      icon: '⏰',
      template: 'Limitierte {product} - Nur solange Vorrat',
      contentType: 'description',
      tone: 'urgent',
      example: 'Sneaker, Uhr, Tasche'
    },
    {
      id: 'retail_opening',
      name: 'Öffnungszeiten',
      icon: '🕐',
      template: 'Öffnungszeiten Information',
      contentType: 'description',
      tone: 'professional',
      example: ''
    }
  ],
  
  hotel: [
    {
      id: 'hotel_welcome',
      name: 'Willkommen',
      icon: '🏨',
      template: 'Willkommens-Text für Hotelgäste',
      contentType: 'description',
      tone: 'elegant',
      example: ''
    },
    {
      id: 'hotel_amenities',
      name: 'Annehmlichkeiten',
      icon: '🛁',
      template: '{amenity} Information',
      contentType: 'description',
      tone: 'professional',
      example: 'Spa, Pool, Restaurant'
    },
    {
      id: 'hotel_event',
      name: 'Event',
      icon: '🎉',
      template: '{event_type} Ankündigung',
      contentType: 'headline',
      tone: 'elegant',
      example: 'Hochzeit, Konferenz, Gala'
    },
    {
      id: 'hotel_special',
      name: 'Special Offer',
      icon: '💝',
      template: '{season} Special Angebot',
      contentType: 'description',
      tone: 'luxury',
      example: 'Romantik, Wellness, Business'
    }
  ],
  
  healthcare: [
    {
      id: 'healthcare_appointment',
      name: 'Termin',
      icon: '📅',
      template: 'Termin-Erinnerung für {service}',
      contentType: 'description',
      tone: 'professional',
      example: 'Vorsorge, Kontrolle, Beratung'
    },
    {
      id: 'healthcare_hours',
      name: 'Sprechzeiten',
      icon: '🕐',
      template: 'Sprechzeiten Information',
      contentType: 'description',
      tone: 'professional',
      example: ''
    },
    {
      id: 'healthcare_service',
      name: 'Leistungen',
      icon: '🏥',
      template: '{service} Leistungsbeschreibung',
      contentType: 'description',
      tone: 'professional',
      example: 'Physiotherapie, Diagnostik'
    }
  ],
  
  education: [
    {
      id: 'education_course',
      name: 'Kurs',
      icon: '📚',
      template: '{course_type} Kurs Ankündigung',
      contentType: 'description',
      tone: 'professional',
      example: 'Sprach-, Computer-, Weiterbildungs-'
    },
    {
      id: 'education_event',
      name: 'Event',
      icon: '🎓',
      template: '{event} Event Ankündigung',
      contentType: 'headline',
      tone: 'energetic',
      example: 'Workshop, Seminar, Vortrag'
    },
    {
      id: 'education_enrollment',
      name: 'Einschreibung',
      icon: '✍️',
      template: 'Einschreibung für {semester}',
      contentType: 'cta',
      tone: 'professional',
      example: 'Sommersemester, Wintersemester'
    }
  ],
  
  office: [
    {
      id: 'office_announcement',
      name: 'Ankündigung',
      icon: '📢',
      template: '{announcement_type} für Mitarbeiter',
      contentType: 'description',
      tone: 'professional',
      example: 'Wichtige Information, Update'
    },
    {
      id: 'office_meeting',
      name: 'Meeting',
      icon: '👥',
      template: 'Meeting Erinnerung für {topic}',
      contentType: 'description',
      tone: 'professional',
      example: 'Team-Meeting, Quartalsbericht'
    },
    {
      id: 'office_welcome',
      name: 'Begrüssung',
      icon: '👋',
      template: 'Begrüssung für {visitor_type}',
      contentType: 'description',
      tone: 'professional',
      example: 'Besucher, Kunden, Partner'
    }
  ],
  
  beauty: [
    {
      id: 'beauty_service',
      name: 'Service',
      icon: '💅',
      template: '{service} Angebot',
      contentType: 'description',
      tone: 'elegant',
      example: 'Maniküre, Pediküre, Massage'
    },
    {
      id: 'beauty_promo',
      name: 'Aktion',
      icon: '💝',
      template: '{promo_type} Special',
      contentType: 'headline',
      tone: 'luxury',
      example: 'Valentinstag, Muttertag'
    },
    {
      id: 'beauty_product',
      name: 'Produkt',
      icon: '✨',
      template: 'Neues {product} Highlight',
      contentType: 'description',
      tone: 'elegant',
      example: 'Serum, Creme, Treatment'
    }
  ],
  
  automotive: [
    {
      id: 'auto_service',
      name: 'Service',
      icon: '🔧',
      template: '{service} Angebot',
      contentType: 'description',
      tone: 'professional',
      example: 'Inspektion, Reparatur, Wartung'
    },
    {
      id: 'auto_sale',
      name: 'Verkauf',
      icon: '🚗',
      template: '{car_type} Angebot',
      contentType: 'headline',
      tone: 'energetic',
      example: 'Neuwagen, Gebrauchtwagen'
    },
    {
      id: 'auto_promo',
      name: 'Aktion',
      icon: '🎁',
      template: '{promo} Aktion',
      contentType: 'headline',
      tone: 'urgent',
      example: 'Winter-Check, Sommer-Service'
    }
  ],
  
  general: [
    {
      id: 'general_welcome',
      name: 'Willkommen',
      icon: '👋',
      template: 'Willkommens-Text',
      contentType: 'description',
      tone: 'professional',
      example: ''
    },
    {
      id: 'general_promo',
      name: 'Aktion',
      icon: '🎯',
      template: '{promo_type} Aktion',
      contentType: 'headline',
      tone: 'energetic',
      example: 'Rabatt, Special, Angebot'
    },
    {
      id: 'general_info',
      name: 'Information',
      icon: 'ℹ️',
      template: '{info_type} Information',
      contentType: 'description',
      tone: 'professional',
      example: 'Öffnungszeiten, Kontakt, Standort'
    },
    {
      id: 'general_cta',
      name: 'Call-to-Action',
      icon: '👆',
      template: 'Handlungsaufforderung für {action}',
      contentType: 'cta',
      tone: 'urgent',
      example: 'Jetzt buchen, Kontaktieren, Besuchen'
    }
  ]
};

// Helper function to get templates for industry
export const getTemplatesForIndustry = (industryId) => {
  return PROMPT_TEMPLATES[industryId] || PROMPT_TEMPLATES.general;
};

// Helper function to find industry by id
export const getIndustryById = (industryId) => {
  return INDUSTRIES.find(i => i.id === industryId) || INDUSTRIES[0];
};

// Helper function to find tone by id
export const getToneById = (toneId) => {
  return TONE_PRESETS.find(t => t.id === toneId) || TONE_PRESETS[0];
};

// Helper function to parse template variables
export const parseTemplateVariables = (template) => {
  const regex = /\{([^}]+)\}/g;
  const variables = [];
  let match;
  
  while ((match = regex.exec(template)) !== null) {
    variables.push(match[1]);
  }
  
  return variables;
};

// Helper function to fill template with values
export const fillTemplate = (template, values) => {
  let filled = template;
  Object.entries(values).forEach(([key, value]) => {
    filled = filled.replace(`{${key}}`, value);
  });
  return filled;
};